import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ICourse } from '.';

export type TGetCourseAsUser = {
  id: string;
  isSubSectionsAllowed?: boolean;
  deviceTimezone?: string;
};

export type TGetCoursesForUser = {
  limit: number;
  offset: number;
  searchText?: string;
  enrolment?: 'ENROLLED' | 'UNENROLLED';
  deviceTimezone?: string;
  isOneToOne?: boolean;
  type?: 'COURSE' | 'CLASS';
  filter?: string;
};

export type TUpdateCourse = {
  _id: string;
  title?: string;
  subTitle?: string;
  description?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
  noOfClasses?: number;
  duration?: number;
  startDate?: string;
  endDate?: string;
  classTime?: string;
  classDays?: string[];
  groupTutorRateCourseType?: string;
};

const apiDataProvider = new APIDataProvider();

const getCoursesForUsersModule = async (data: TGetCoursesForUser) => {
  let path = `${ApiPaths.courses}?limit=${data.limit}&offset=${data.offset}`;

  if (data.searchText) {
    path += `&searchText=${encodeURIComponent(data.searchText)}`;
  }

  if (data.enrolment) {
    path += `&enrolment=${data.enrolment}`;
  }

  if (data.deviceTimezone) {
    path += `&deviceTimezone=${data.deviceTimezone}`;
  }

  if (data.isOneToOne !== undefined) {
    path += `&isOneToOne=${data.isOneToOne}`;
  }

  if (data.type) {
    path += `&type=${data.type}`;
  }

  if (data.filter) {
    path += `&filter=${data.filter}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICourse>>
  >(path);

  return response.data;
};

export const getEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/enrolment-list',
  getCoursesForUsersModule
);

export const getEnrolmentCoursesForUsersForNotification = createAsyncThunk(
  '/user/courses/enrolment-list-for-notification',
  getCoursesForUsersModule
);

export const loadMoreEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/enrolment-loadMore',
  getCoursesForUsersModule
);

export const getNonEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/non-enrolment-list',
  getCoursesForUsersModule
);

export const loadMoreNonEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/non-enrolment-loadMore',
  getCoursesForUsersModule
);

export const getCourseByIdForUsers = createAsyncThunk(
  '/user/courses/getOne',
  async (data: TGetCourseAsUser) => {
    let path = `${ApiPaths.courses}/${data.id}?subSections=${
      data.isSubSectionsAllowed ? '1' : '0'
    }`;

    if (data.deviceTimezone) {
      path += `&deviceTimezone=${data.deviceTimezone}`;
    }

    const response = await apiDataProvider.get<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const updateCourse = createAsyncThunk(
  '/user/courses/updateOne',
  async ({ _id, ...data }: TUpdateCourse) => {
    const path = `${ApiPaths.courses}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ICourse>>(
      path,
      data
    );

    return response.data;
  }
);
